<script lang="ts">
  // A single comment, compact: avatar + inline "Name · time" header (no divider),
  // the body, any images, and a small like row.
  //
  //   • default (bare): no card of its own, so a whole thread can share one card —
  //     the surrounding <CommentThread> supplies the container, indentation and the
  //     vertical connector line.
  //   • with `onclick`: renders as its own clickable card (stretched-link), e.g. a
  //     standalone comment preview in the moderation inbox.
  import Avatar from './Avatar.svelte';
  import Upvote from './Upvote.svelte';
  import { followLink } from './host';
  import MemberHover from './MemberHover.svelte';
  import Clamp from './Clamp.svelte';
  import SkoolContent from './SkoolContent.svelte';
  import SkoolMedia from './SkoolMedia.svelte';
  import DeletedBadge from './DeletedBadge.svelte';
  import type { Snippet } from 'svelte';
  import { extractImages, stripImages } from './skoolContent';
  import { timeAgo, timeAgoShort, type PostAuthor, type ImageMedia, type VideoMedia } from './member';

  let {
    author = null,
    body = null,
    images = null,
    videos = null,
    skoolUrl = null,
    upvotes = 0,
    createdAt = null,
    selected = false,
    onReply = null,
    liked = false,
    onLike = null,
    replyActive = false,
    now = Date.now(),
    onclick = null,
    boxed = null,
    replyCount = null,
    pinned = false,
    mediaSide = false,
    badges = null,
    actions = null,
    deleted = false,
  }: {
    /** Gone from Skool. Renders the row tinted and badged rather than hiding it — a
     *  deleted comment is still the reason a queue row exists. See `<DeletedBadge>`. */
    deleted?: boolean;
    author?: PostAuthor | null;
    body?: string | null;
    /** Stored images (uploads + gifs). Falls back to images parsed from the body. */
    images?: ImageMedia[] | null | undefined;
    /** Stored videos (Skool-native posters + external embeds). */
    videos?: VideoMedia[] | null | undefined;
    /** Parent post's URL on Skool — the deep-link target for a native video. */
    skoolUrl?: string | null;
    upvotes?: number | null;
    createdAt?: string | null;
    selected?: boolean;
    /**
     * Ask to reply to THIS one. Given, a "Reply" affordance shows and calling it is
     * all this card does — the composer itself is mounted once by whoever owns the
     * thread, so there is one reply box on screen and one code path behind it.
     *
     * It used to mount its own `<ReplyComposer>` here, which never worked from a
     * thread: `CommentThread` forwarded the community but not the submit callback, so
     * the box opened with no send button at all — the composer's action row is gated
     * on having somewhere to send.
     */
    onReply?: (() => void) | null;
    /** Already liked on Skool. Renders the thumb filled and inert — there is no
     *  unlike, here or anywhere: see `<Upvote>`. */
    liked?: boolean;
    /** Perform the like. Absent on any surface that cannot write to Skool, which is
     *  the dashboard unless a Helper in the same browser can be asked to do it. */
    onLike?: (() => Promise<void> | void) | null;
    /** True when the one composer is currently open on this card. */
    replyActive?: boolean;
    now?: number;
    onclick?: (() => void) | null;
    /**
     * Draw the card's own box — border, background, padding.
     *
     * `null` (default) means "whatever `onclick` implies", which is how this component
     * behaved when a click target was the only reason to be boxed: a comment in a
     * <CommentThread> is a bare bubble, indented under its parent and joined by a
     * connector line, and a border on each one is noise inside the post's own box.
     *
     * That conflated two different things, and a standalone LIST of comments — the
     * member page's archive, the pipeline card drawer's — needs the box without the
     * click, because there each comment is its own card with nothing else framing it.
     * Pass `true` there. Set explicitly, `boxed` wins over the inference.
     */
    boxed?: boolean | null;
    /**
     * How many direct replies this comment drew (`reply_count` on the wire).
     *
     * `null` means NOT COUNTED and renders nothing — a route that counted and found
     * none sends 0, which renders as "0". The two are different claims and the chip
     * must not invent the second from the first.
     */
    replyCount?: number | null;
    /** Show a "📌 Pinned" badge — the owner pinned this comment on the post. */
    pinned?: boolean;
    /** Summary previews only: render media as a compact square to the RIGHT of the
     *  text (instead of below) so the card height stays governed by the text. */
    mediaSide?: boolean;
    /** Optional chip row rendered at the very top of the card (e.g. moderation context). */
    badges?: Snippet | null;
    /** Optional footer actions, rendered right of the Skool link (above the click layer). */
    actions?: Snippet | null;
  } = $props();

  const authorName = $derived(author?.name || author?.handle || 'Unknown');
  const mediaImages = $derived<ImageMedia[]>(
    images && images.length
      ? images
      : extractImages(body).map((url) => ({ url, width: null, height: null })),
  );
  const hasMedia = $derived(mediaImages.length > 0 || (videos?.length ?? 0) > 0);
  const textBody = $derived(stripImages(body));

  // Inline reply is offered only on a non-clickable (thread) comment.
  /**
   * NOTHING TO REPLY TO. Skool has no such comment any more, so a reply addressed to
   * it cannot be filed — its id is exactly what `parent_id` would carry. Offering the
   * control and failing on send would be worse than not offering it: the moderator
   * writes the answer first and learns afterwards.
   *
   * The row still CLEARS. It stays in the queue and the deck still walks it, because
   * "this is dealt with" is the one thing left that can be true about it.
   */
  const canReply = $derived(!!onReply && !onclick && !deleted);

  const isBoxed = $derived(boxed ?? onclick != null);
  /** A nested reply is supporting material — cap it harder than a top-level one. */
  const compactBody = $derived(!isBoxed);
  // The hover lift is an affordance, so it belongs to the clickable card only — on a
  // boxed-but-inert one it would promise an interaction that isn't there.
  const stateCls = $derived(
    selected
      ? 'border-primary/50 bg-primary/[0.05] shadow-sm shadow-primary/5'
      : onclick
        ? 'hair shadow-sm shadow-black/[0.04] transition-all hover:border-base-content/20 hover:shadow-md hover:shadow-black/[0.07]'
        : 'hair shadow-sm shadow-black/[0.04]',
  );
</script>

{#snippet meta()}
  <div class="mt-1 flex items-center gap-3 pl-1 text-[0.7rem] text-base-content/62">
    <Upvote count={upvotes} {liked} onLike={onLike} />
    {#if canReply}
      <button
        type="button"
        class="font-semibold transition-colors hover:text-primary {replyActive ? 'text-primary' : ''}"
        onclick={() => onReply?.()}
      >Reply</button>
    {/if}
    {#if skoolUrl || actions}
      <div class="relative z-10 ml-auto flex items-center gap-3">
        {#if skoolUrl}
          <a href={skoolUrl} target="_blank" rel="noopener noreferrer" onclick={(e) => followLink(e, skoolUrl)} class="opacity-0 transition-opacity hover:text-primary group-hover/c:opacity-100" title="Open on Skool">Skool</a>
        {/if}
        {#if actions}{@render actions()}{/if}
      </div>
    {/if}
  </div>
{/snippet}

<!--
  THREAD MODE — a Skool comment, shaped like one.

  Avatar OUTSIDE a soft bubble that holds the name, the time and the body; the two
  actions sit under the bubble. That shape is doing real work: the fill is what says
  where a comment starts and stops, which a borderless block of text at full width
  never did, and it says it without a border — so a thread of forty reads as a
  conversation rather than as forty filed cards.

  It also strips what a thread repeats: no reply-count chip (the replies are directly
  underneath), a short "19d" rather than "19 hours ago", and the Skool link only on
  hover. Forty comments each carrying four pieces of chrome IS the clutter.
-->
{#snippet bubble()}
  <div class="group/c flex gap-2.5">
    {#if author?.member_id != null && author.member_id > 0}
      <MemberHover memberId={author.member_id} class="relative z-10 mt-0.5 shrink-0">
        <Avatar src={author?.picture_profile ?? null} name={authorName} size={28} memberId={author.member_id} />
      </MemberHover>
    {:else}
      <Avatar src={author?.picture_profile ?? null} name={authorName} size={28} />
    {/if}

    <div class="min-w-0 flex-1">
      {#if badges}
        <div class="mb-1 flex flex-wrap items-center gap-1.5">{@render badges()}</div>
      {/if}

      <!-- A TINT OF THE FOREGROUND, not `base-200`: this theme's base-200 is within a
             hair of base-100, so the bubble was invisible and the comment read as loose
             text again. `base-content/[0.055]` is dark on a light theme and light on a
             dark one, so the shape survives both. -->
        <div
          class="rounded-2xl px-3 py-2 {deleted
            ? 'bg-error/[0.07] ring-1 ring-inset ring-error/20'
            : 'bg-base-content/[0.055]'}"
        >
        <div class="flex flex-wrap items-baseline gap-x-1.5">
          <span class="redact truncate text-[0.8rem] font-semibold text-base-content">{authorName}</span>
          {#if deleted}<DeletedBadge kind="comment" />{/if}
          {#if createdAt}
            <span class="shrink-0 text-[0.7rem] text-base-content/55">· {timeAgoShort(createdAt, now)}</span>
          {/if}
          {#if pinned}
            <span class="shrink-0 text-[0.68rem] font-medium text-primary" title="Pinned by the owner">📌 Pinned</span>
          {/if}
        </div>

        {#if textBody}
          <div class="mt-0.5">
            <Clamp max={132}>
              <SkoolContent text={textBody} class="text-sm leading-relaxed text-base-content/90" />
            </Clamp>
          </div>
        {/if}
        {#if hasMedia}
          <div class="mt-2">
            <SkoolMedia images={mediaImages} {videos} clamped {skoolUrl} interactive={!onclick} />
          </div>
        {/if}
      </div>

      {@render meta()}
    </div>
  </div>
{/snippet}

{#snippet content()}
  {#if badges}
    <div class="mb-2 flex flex-wrap items-center gap-1.5">{@render badges()}</div>
  {/if}
  <!-- header: avatar + inline name · time -->
  <div class="flex items-center gap-2.5">
    <!-- `> 0`: a reply the panel has only just sent has no member row yet and carries
         0 as its author id. A hover card for member 0 is a spinner that never resolves. -->
    {#if author?.member_id != null && author.member_id > 0}
      <MemberHover memberId={author.member_id} class="relative z-10 shrink-0">
        <Avatar src={author?.picture_profile ?? null} name={authorName} size={28} memberId={author.member_id} />
      </MemberHover>
    {:else}
      <Avatar src={author?.picture_profile ?? null} name={authorName} size={28} />
    {/if}
    <div class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-1.5 text-[0.8rem] leading-tight">
      <span class="redact truncate font-semibold text-base-content">{authorName}</span>
      {#if createdAt}
        <span class="shrink-0 text-base-content/58">·</span>
        <span class="shrink-0 text-[0.72rem] text-base-content/75">{timeAgo(createdAt, now)}</span>
      {/if}
      {#if pinned}
        <span class="badge badge-xs badge-primary shrink-0 gap-0.5 font-medium" title="Pinned by the owner">📌 Pinned</span>
      {/if}
    </div>
  </div>

  {#if mediaSide && hasMedia}
    <!-- Summary side layout: text left (governs height), media square on the right. -->
    <div class="mt-2 flex items-start gap-3">
      <div class="min-w-0 flex-1">
        {#if textBody}
          <SkoolContent text={textBody} class="text-sm leading-relaxed text-base-content/85" />
        {/if}
      </div>
      <SkoolMedia images={mediaImages} {videos} side {skoolUrl} interactive={!onclick} />
    </div>
  {:else}
    {#if textBody}
      <div class="mt-2">
        <Clamp max={compactBody ? 132 : 180}>
          <SkoolContent text={textBody} class="text-sm leading-relaxed text-base-content/85" />
        </Clamp>
      </div>
    {/if}
    {#if hasMedia}
      <!-- Small tiles, capped at four with a "+N". A comment's media is supporting
           material — at full size, three GIF replies filled a 340px panel and the
           conversation they belong to scrolled off it. The lightbox still pages
           through every one. -->
      <div class="mt-2">
        <SkoolMedia
          images={mediaImages}
          {videos}
          clamped={compactBody}
          {skoolUrl}
          interactive={!onclick}
        />
      </div>
    {/if}
  {/if}

  <div class="mt-2 flex items-center gap-3 text-[0.7rem] text-base-content/75">
    <Upvote count={upvotes} {liked} onLike={onLike} />
    {#if replyCount != null}
      <!-- The same speech-bubble chip <SkoolPost> uses for its comment count: on a
           comment the analogous number is its replies, so the two read as one scale. -->
      <span class="inline-flex items-center gap-1" title="{replyCount} {replyCount === 1 ? 'reply' : 'replies'}">
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
        {replyCount}
      </span>
    {/if}
    {#if canReply}
      <button
        type="button"
        class="inline-flex items-center gap-1 font-medium transition-colors hover:text-primary {replyActive ? 'text-primary' : ''}"
        onclick={() => onReply?.()}
      >
        <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>
        Reply
      </button>
    {/if}
    {#if skoolUrl || actions}
      <!-- relative z-10: sit above the onclick-card's stretched click layer. -->
      <div class="relative z-10 ml-auto flex items-center gap-3">
        {#if skoolUrl}
          <a href={skoolUrl} target="_blank" rel="noopener noreferrer" onclick={(e) => followLink(e, skoolUrl)} class="inline-flex items-center gap-1 transition-colors hover:text-primary" title="Open on Skool">
            Skool
            <svg class="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17 17 7" /><polyline points="7 7 17 7 17 17" /></svg>
          </a>
        {/if}
        {#if actions}{@render actions()}{/if}
      </div>
    {/if}
  </div>

  {/snippet}

{#if isBoxed}
  <div class="relative rounded-box border bg-base-100 px-3.5 py-3 {onclick ? 'cursor-pointer' : ''} {stateCls}">
    {@render content()}
    {#if onclick}
      <button
        type="button"
        class="absolute inset-0 z-[5] rounded-box focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        aria-label="Open comment"
        {onclick}
      ></button>
    {/if}
  </div>
{:else}
  {@render bubble()}
{/if}
